import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { Observable } from 'rxjs';
import { User } from '../models/user-manager/user';
import { ApiResponse } from '@card-software/library';
import { USE_ALL_SITES } from '@card-software/nexus-utilities';

/**
 * Asks the shared error interceptor not to toast a failure, for calls whose caller shows its own message
 * (e.g. "User not available" when a delegated user manager gets a 404, #6263).
 */
const CALLER_HANDLES_ERRORS = new HttpHeaders({ 'Skip-Error-Display': 'true' });

@Injectable({ providedIn: 'root' })
export class UserProxyService {
  private baseUrl = `${environment.proxyUrls.userMasterApi}/api/v1/user`;

  constructor(private httpClient: HttpClient) {}

  getAllUsers(): Observable<ApiResponse<User[]>> {
    return this.httpClient.get<ApiResponse<User[]>>(`${this.baseUrl}/all`);
  }

  searchUsers(username?: string, groupIds?: string[], useAllSites = false): Observable<ApiResponse<User[]>> {
    const context = new HttpContext().set(USE_ALL_SITES, useAllSites);
    return this.httpClient.post<ApiResponse<User[]>>(`${this.baseUrl}/search`, { username, groupIds }, { context });
  }

  /** @param callerHandlesErrors the caller shows its own message on failure (no interceptor toast). */
  getUserById(id: string, callerHandlesErrors = false): Observable<ApiResponse<User>> {
    return this.httpClient.get<ApiResponse<User>>(`${this.baseUrl}/${id}`, this.errorOptions(callerHandlesErrors));
  }

  updateProfile(user: User, callerHandlesErrors = false) {
    return this.httpClient.put(`${this.baseUrl}/${user.id}/update-profile`, user.profile, this.errorOptions(callerHandlesErrors));
  }
  updatePassword(user: User, callerHandlesErrors = false) {
    const password = user.password!;

    return this.httpClient.put(`${this.baseUrl}/${user.id}/update-password`, `"${password}"`, this.errorOptions(callerHandlesErrors));
  }

  updateGroupsAndRoles(user: User, siteId?: string, callerHandlesErrors = false) {
    const sp = siteId ? user.siteProperties?.find((s) => s.siteId === siteId) : user.siteProperties?.[0];
    return this.httpClient.put(
      `${this.baseUrl}/${user.id}/update-groups-roles`,
      {
        userSiteProperties: sp,
      },
      this.errorOptions(callerHandlesErrors),
    );
  }

  updateStatus(user: User, callerHandlesErrors = false) {
    return this.httpClient.put(`${this.baseUrl}/${user.id}/update-status`, user.status, this.errorOptions(callerHandlesErrors));
  }

  /** Internal admins only (the API answers 403 to delegates): whether external user admins manage the user (#6295). */
  updateExternallyManaged(user: User) {
    return this.httpClient.put(`${this.baseUrl}/${user.id}/external-management`, user.isExternallyManaged ?? false);
  }

  removeSite(userId: string, siteId: string) {
    return this.httpClient.delete(`${this.baseUrl}/${userId}/sites/${siteId}`);
  }

  createUser(user: User): Observable<ApiResponse<User>> {
    return this.httpClient.post<ApiResponse<User>>(`${this.baseUrl}/add`, user);
  }

  private errorOptions(callerHandlesErrors: boolean): { headers?: HttpHeaders } {
    return callerHandlesErrors ? { headers: CALLER_HANDLES_ERRORS } : {};
  }
}
